import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import MainLayout from '../../layouts/MainLayout';
import "../../styles/MyProfile.css";
import ChatGPTResumeComponent from '../../components/ChatGPTResumeComponent';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBriefcase, 
  faUsers, 
  faIdCard, 
  faSearch,
  faUserPlus,
  faCalendarCheck,
  faUserCircle,
  faUserTie,
  faUserShield,
  faMapMarkedAlt,
  faTasks,
  faShieldAlt,
  faIdBadge,
  faPhoneAlt
} from '@fortawesome/free-solid-svg-icons';

const BlueHandsMyBioData = () => {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [fetchData] = useFetch();
  const userId = authState.user._id;

  const [profileData, setProfileData] = useState({
    name: authState.user.name,
    gender: '',  
    dob: null, 
    email: authState.user.email,
    mobile: '',
    outlookDetails: '',  
    address: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    languageSpeaks: '',
    languageWrites: '',
    qualification: '',

    jobTitle: '',        
    department: '',      
    currentCompany: '',  
    doJoining: null,       

    isAvailable: true, 
    availableForWork: '',
    availabilityRemarks: '', 
    socialLinks: '',
    personalPageLink: '',
    bio: '',

    // feedback: [], 

    minAmount: 0,
    maxAmount: 0,
    currency: 'Rs.',
    amtTenure: 'Monthly',
    isNegotiable: false,
    prefTiming: '',
    overtimeChoice: '',
    myPreferredCategory: '',
    myPreferredLocation: '',
    myKeywords: '',

    currProject: "",
    currSite: "",
    currLocality: "",    
    currPlace: "",
    currPS: "",
    currHospitals: "",
    currLocalBody: "",
    currSubDistrict: "",
    currDistrict: "",
    currRegion: "",
    currState: "",
    currCountry: "",
    currPIN: "",
  });

  const [originalProfileData, setOriginalProfileData] = useState({});
  const [profileExists, setProfileExists] = useState(false);
  const [profileId, setProfileId] = useState('');
  const [loading, setLoading] = useState(true);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [createSuccessMsg, setCreateSuccessMsg] = useState('');
  const [updateSuccessMsg, setUpdateSuccessMsg] = useState('');
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUserProfile(userId);
  }, [userId]);

  const fetchUserProfile = useCallback(() => {
    try {
      const config = {
        url: "/blueprofile/forresume",
        method: 'post',
        data: { userId: userId },
        headers: { Authorization: authState.token },
      };
      fetchData(config)
        .then(response => {
          if (response && response.status) {
            // console.log("profile exists ",response);
            setProfileExists(true);
            const { blueProfile } = response;
            setProfileId(blueProfile._id);
            setProfileData(blueProfile);
          } else {
            setCreateSuccessMsg("Sorry! Profile Not Found. Please retry.");
          }
        })
        .catch(() => {
          setCreateSuccessMsg("Sorry! Profile Not Found. Please retry.");
        });
    } catch {
      setCreateSuccessMsg("Sorry! Profile not found. Please retry.");
    }
  }, [userId, fetchData, setProfileExists]);

  const [createPromptClicked, setCreatePromptClicked] = useState(false);
  const handleCreatePrompt = () => {
    setCreatePromptClicked(!createPromptClicked);
  }

  const validateInputs = () => {
    let isValid = true;
    const errors = {};
    const mobileRegex = /^\d{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (profileData.gender === undefined || profileData.gender === null) {
      errors.gender = 'Gender is required.';
      isValid = false;
    }

    if (!profileData.dob) {
      errors.dob = 'Date of Birth is required.';
      isValid = false;
    }

    if (!emailRegex.test(profileData.email)) {
      setMessage("Please enter a valid email address.");
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  };

  const generateResumePrompt = (profile) => {
    return `Write a professional resume based on the following details:
  
    Name: ${profile.name}
    Gender: ${profile.gender || "Not Specified"}
    Date of Birth: ${profile.dob || "Not Provided"}
    Email: ${profile.email}
    Mobile: ${profile.mobile || "Not Provided"}
    Address: ${profile.address}, ${profile.city}, ${profile.state}, ${profile.country} - ${profile.pincode}
  
    **Language Details**:
    Languages Speaks: ${profile.languageSpeaks || "Not Provided"}
    Languages Writes: ${profile.languageWrites || "Not Provided"}

    **Qualification Details**:
    Qualification: ${profile.qualification || "Not Provided"}

    **Career Details**:
    Job Title: ${profile.jobTitle || "Not Provided"}
    Department: ${profile.department || "Not Provided"}
    Current Company: ${profile.currentCompany || "Not Provided"}
    Date of Joining: ${profile.doJoining || "Not Provided"}
    
    **Availability**:
    Available for Work: ${profile.availableForWork || "Not Provided"}
    Availability Remarks: ${profile.availabilityRemarks || "Not Provided"}
  
    **Social & Professional Links**:
    Social Links: ${profile.socialLinks || "Not Provided"}
    Personal Page: ${profile.personalPageLink || "Not Provided"}
  
    **Bio & Work Preferences**:
    Bio: ${profile.bio || "Not Provided"}
    Preferred Category: ${profile.myPreferredCategory || "Not Provided"}
    Preferred Location: ${profile.myPreferredLocation || "Not Provided"}
    Keywords: ${profile.myKeywords || "Not Provided"}
  
    **Compensation & Work Preferences**:
    Minimum Salary: ${profile.currency} ${profile.minAmount}
    Maximum Salary: ${profile.currency} ${profile.maxAmount}
    Amount Tenure: ${profile.amtTenure}
    Is Negotiable: ${profile.isNegotiable ? "Yes" : "No"}
    Preferred Timing: ${profile.prefTiming || "Not Provided"}
    Overtime Choice: ${profile.overtimeChoice || "Not Provided"}
  
    **Current Work Location**:
    Project: ${profile.currProject || "Not Provided"}
    Site: ${profile.currSite || "Not Provided"}
    Locality: ${profile.currLocality || "Not Provided"}
    Place: ${profile.currPlace || "Not Provided"}
    Police Station: ${profile.currPS || "Not Provided"}
    Hospitals Nearby: ${profile.currHospitals || "Not Provided"}
    Local Body: ${profile.currLocalBody || "Not Provided"}
    Sub-District: ${profile.currSubDistrict || "Not Provided"}
    District: ${profile.currDistrict || "Not Provided"}
    Region: ${profile.currRegion || "Not Provided"}
    State: ${profile.currState || "Not Provided"}
    Country: ${profile.currCountry || "Not Provided"}
    PIN: ${profile.currPIN || "Not Provided"}
  
    Please create a resume using a professional format, ensuring clarity, proper sections, and a polished tone.`;
  };
  

  return (
    <MainLayout>
      <div className="text-lg mt-12 mb-2 mx-8">
        <h2 className="text-2xl font-bold text-white text-center">My AI Resume</h2>
      </div>
      <div className="min-h-[50vh] mt-4 border border-b-gray-300 p-4 rounded-lg bg-gray-800 text-white">
        {!profileExists ? (
          <div className="text-lg text-yellow-200">
            Your Blue Profile Does Not Exist!!
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col border border-purple-400 py-2 px-2">
              <label className="text-yellow-200">Name: {profileData.name}</label>
            </div>
            {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(profileData).map(([key, value]) => (
                <div key={key} className="flex flex-col border border-gray-600 p-2 rounded-lg">
                  <label className="text-yellow-300 capitalize">{key.replace(/([A-Z])/g, ' $1')}:</label>
                  <span className="text-white">
                    {value !== null && value !== "" ? value.toString() : "N/A"}
                  </span>
                </div>
              ))}
            </div> */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col border border-purple-400 py-2 px-2">
                <label className="text-yellow-200">Name: {profileData.name}</label>
                <label className="text-yellow-200">Gender: {profileData.gender}</label>
                <label className="text-yellow-200">Date of Birth: {profileData.dob}</label>
                <label className="text-yellow-200">Email: {profileData.email}</label>
                <label className="text-yellow-200">Mobile: {profileData.mobile}</label>
                <label className="text-yellow-200">Outlook Details: {profileData.outlookDetails}</label>
                <label className="text-yellow-200">Address: {profileData.address}</label>
                <label className="text-yellow-200">City: {profileData.city}</label>
                <label className="text-yellow-200">State: {profileData.state}</label>
                <label className="text-yellow-200">Country: {profileData.country}</label>
                <label className="text-yellow-200">Pincode: {profileData.pincode}</label>
                <label className="text-yellow-200">Languages Speaks: {profileData.languageSpeaks}</label>
                <label className="text-yellow-200">Languages Writes: {profileData.languageWrites}</label>
                <label className="text-yellow-200">Qualification: {profileData.qualification}</label>                
              </div>

              <div className="flex flex-col border border-green-400 py-2 px-2">
                <label className="text-yellow-200">Job Title: {profileData.jobTitle}</label>
                <label className="text-yellow-200">Department: {profileData.department}</label>
                <label className="text-yellow-200">Current Company: {profileData.currentCompany}</label>
                <label className="text-yellow-200">Date of Joining: {profileData.doJoining}</label>
              </div>

              <div className="flex flex-col border border-blue-400 py-2 px-2">
                <label className="text-yellow-200">Availability: {profileData.isAvailable ? "Yes" : "No"}</label>
                <label className="text-yellow-200">Available For Work: {profileData.availableForWork}</label>
                <label className="text-yellow-200">Availability Remarks: {profileData.availabilityRemarks}</label>
              </div>

              <div className="flex flex-col border border-red-400 py-2 px-2">
                <label className="text-yellow-200">Social Links: {profileData.socialLinks}</label>
                <label className="text-yellow-200">Personal Page Link: {profileData.personalPageLink}</label>
                <label className="text-yellow-200">Bio: {profileData.bio}</label>
              </div>

              <div className="flex flex-col border border-orange-400 py-2 px-2">
                <label className="text-yellow-200">Min Amount: {profileData.minAmount}</label>
                <label className="text-yellow-200">Max Amount: {profileData.maxAmount}</label>
                <label className="text-yellow-200">Currency: {profileData.currency}</label>
                <label className="text-yellow-200">Amount Tenure: {profileData.amtTenure}</label>
                <label className="text-yellow-200">Negotiable: {profileData.isNegotiable ? "Yes" : "No"}</label>
                <label className="text-yellow-200">Preferred Timing: {profileData.prefTiming}</label>
              </div>

              <div className="flex flex-col border border-yellow-400 py-2 px-2">
                <label className="text-yellow-200">Overtime Choice: {profileData.overtimeChoice}</label>
                <label className="text-yellow-200">Preferred Category: {profileData.myPreferredCategory}</label>
                <label className="text-yellow-200">Preferred Location: {profileData.myPreferredLocation}</label>
                <label className="text-yellow-200">Keywords: {profileData.myKeywords}</label>
              </div>

              <div className="flex flex-col border border-pink-400 py-2 px-2">
                <label className="text-yellow-200">Current Project: {profileData.currProject}</label>
                <label className="text-yellow-200">Current Site: {profileData.currSite}</label>
                <label className="text-yellow-200">Current Locality: {profileData.currLocality}</label>
                <label className="text-yellow-200">Current Place: {profileData.currPlace}</label>
                <label className="text-yellow-200">Current PS: {profileData.currPS}</label>
                <label className="text-yellow-200">Current Hospitals: {profileData.currHospitals}</label>
                <label className="text-yellow-200">Current Local Body: {profileData.currLocalBody}</label>
                <label className="text-yellow-200">Current Sub-District: {profileData.currSubDistrict}</label>
                <label className="text-yellow-200">Current District: {profileData.currDistrict}</label>
                <label className="text-yellow-200">Current Region: {profileData.currRegion}</label>
                <label className="text-yellow-200">Current State: {profileData.currState}</label>
                <label className="text-yellow-200">Current Country: {profileData.currCountry}</label>
                <label className="text-yellow-200">Current PIN: {profileData.currPIN}</label>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <button
                className="update-profile-button px-4 py-2 bg-green-600 text-white hover:bg-green-700 w-40 mr-4"
                onClick={handleCreatePrompt}
              >
                Create Prompt
              </button>
              <button
                className="update-profile-button px-4 py-2 bg-red-600 text-white hover:bg-red-700 w-40"
                onClick={() => navigate(-1)}
              >
                Back
              </button>
            </div>
            {createPromptClicked && (
              <div className="mb-1">
                <ChatGPTResumeComponent param1={"Blue Collar Worker Resume "} param2={generateResumePrompt(profileData)}/>
              </div>
            )}
          </div> 
        )} 
      </div>
    </MainLayout>

  );
};

export default BlueHandsMyBioData;



